import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useAddTerminalInventoryItemMutation } from "Api/Mutations/TerminalInventory/useAddTerminalInventoryItemMutation";
import { useDeleteTerminalInventoryItemMutation } from "Api/Mutations/TerminalInventory/useDeleteTerminalInventoryItemMutation";
import { useUpdateTerminalInventoryItemMutation } from "Api/Mutations/TerminalInventory/useUpdateTerminalInventoryItemMutation";
import {
  type EditTerminalFormData,
  getEditTerminalSchema,
} from "Components/Admin/Terminals/EditTerminalDialog/editTerminalSchema";
import { getStatusOptions } from "Components/Admin/Terminals/EditTerminalDialog/getStatusOptions";
import { HunterAutocomplete } from "Components/Admin/Terminals/EditTerminalDialog/HunterAutocomplete";
import {
  CURRENCY_OPTIONS,
  KNOWN_HARDWARE_MODELS,
} from "Components/Admin/Terminals/EditTerminalDialog/terminalConstants";
import { TerminalInfoGrid } from "Components/Admin/Terminals/EditTerminalDialog/TerminalInfoGrid";
import { TerminalAuditSection } from "Components/Admin/Terminals/TerminalAuditSection";
import {
  TerminalStatusChip,
  useTerminalStatusLabels,
} from "Components/Admin/Terminals/TerminalStatusChip";
import {
  CurrencyCode,
  TerminalInventoryStatus,
  type TerminalInventoryListItemDto,
} from "Infrastructure/Api/Api";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Admin.Terminals;

type Props = {
  isOpen: boolean;
  /** Null = stock in a new piece with the same surface. */
  terminal: TerminalInventoryListItemDto | null;
  canDelete?: boolean;
  onClose: () => void;
};

export const EditTerminalDialog = ({
  isOpen,
  terminal,
  canDelete = false,
  onClose,
}: Props) => {
  const { t } = useResource();
  const statusLabels = useTerminalStatusLabels();

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm<EditTerminalFormData>({
    resolver: zodResolver(getEditTerminalSchema(t)),
    defaultValues: {
      serialNumber: terminal?.serialNumber ?? "",
      hardwareModel: terminal?.hardwareModel ?? "",
      hunter: terminal?.hunterPartyPublicID
        ? {
            partyPublicID: terminal.hunterPartyPublicID,
            label: terminal.hunterName ?? "",
          }
        : null,
      purchasePrice: terminal?.purchasePrice ?? null,
      purchasePriceCurrencyCode:
        terminal?.purchasePriceCurrencyCode ?? CurrencyCode.CZK,
      status: terminal?.status ?? TerminalInventoryStatus.InStock,
    },
  });

  const selectedHunter = watch("hunter");
  const selectedModel = watch("hardwareModel");

  const handleError = (error: { status: number }) => {
    toast.error(
      error.status === 409
        ? t(RESOURCES.Messages.DuplicateSerialNumber)
        : t(RESOURCES.Messages.UpdateError),
    );
  };

  const { mutateAsync: addTerminal, isPending: isAddPending } =
    useAddTerminalInventoryItemMutation({ onError: handleError });
  const { mutateAsync: updateTerminal, isPending: isUpdatePending } =
    useUpdateTerminalInventoryItemMutation({ onError: handleError });
  const { mutate: deleteTerminal, isPending: isDeletePending } =
    useDeleteTerminalInventoryItemMutation({
      onSuccess: () => {
        toast.success(t(RESOURCES.Messages.DeleteSuccess));
        onClose();
      },
      onError: () => {
        toast.error(t(RESOURCES.Messages.DeleteError));
      },
    });

  const isCreate = terminal === null;
  const hasMerchant = !!terminal?.merchantPublicID;
  const isTmsRegistered = !!terminal?.tid;

  // Legacy pieces may carry a model outside the known list — keep it selectable.
  const modelOptions =
    selectedModel && !KNOWN_HARDWARE_MODELS.includes(selectedModel)
      ? [selectedModel, ...KNOWN_HARDWARE_MODELS]
      : KNOWN_HARDWARE_MODELS;

  const statusOptions = getStatusOptions({
    hasMerchant,
    hasHunter: !!selectedHunter,
    isTmsRegistered,
  });

  const handleHunterChange = (hunter: EditTerminalFormData["hunter"]) => {
    setValue("hunter", hunter);
    const status = getValues("status");
    if (!hasMerchant) {
      // Stock statuses are derived from ownership; a parked (Returned) piece stays parked.
      if (status !== TerminalInventoryStatus.Returned) {
        setValue(
          "status",
          hunter
            ? TerminalInventoryStatus.AssignedToHunter
            : TerminalInventoryStatus.InStock,
        );
      }
    } else if (hunter && status === TerminalInventoryStatus.Shipped) {
      setValue("status", TerminalInventoryStatus.AssignedToMerchant);
    }
  };

  const handleDelete = () => {
    if (
      terminal &&
      window.confirm(
        t(RESOURCES.Messages.DeleteConfirm, {
          serialNumber: terminal.serialNumber,
        }),
      )
    ) {
      deleteTerminal(terminal.publicID);
    }
  };

  const onSubmit = async ({
    serialNumber,
    hardwareModel,
    purchasePrice = null,
    purchasePriceCurrencyCode: priceCurrencyCode,
    hunter,
    status,
  }: EditTerminalFormData) => {
    // A currency without a price is dead data — the BE rejects the pair.
    const purchasePriceCurrencyCode =
      purchasePrice == null ? null : priceCurrencyCode;
    const terminalData = {
      hardwareModel,
      purchasePrice,
      purchasePriceCurrencyCode,
      hunterPartyPublicID: hunter?.partyPublicID ?? null,
      status,
    };

    try {
      if (isCreate) {
        await addTerminal({ serialNumber, ...terminalData });
        toast.success(t(RESOURCES.Messages.CreateSuccess));
      } else {
        await updateTerminal({
          publicID: terminal.publicID,
          request: terminalData,
        });
        toast.success(t(RESOURCES.Messages.UpdateSuccess));
      }
      onClose();
    } catch {
      // Error toasts are handled in the mutation hooks.
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <span>
              {isCreate ? t(RESOURCES.Form.AddTitle) : terminal.serialNumber}
            </span>
            {!isCreate && <TerminalStatusChip status={terminal.status} />}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack gap={3} divider={<Divider />}>
            {!isCreate && (
              <TerminalInfoGrid
                terminal={terminal}
                isHunterFinanced={!!selectedHunter}
              />
            )}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  control={control}
                  name="serialNumber"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t(RESOURCES.Form.SerialNumber)}
                      error={!!errors.serialNumber}
                      helperText={errors.serialNumber?.message}
                      size="small"
                      fullWidth
                      // The serial number identifies the physical piece — set only at stock-in.
                      disabled={!isCreate}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  control={control}
                  name="hardwareModel"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label={t(RESOURCES.Form.HardwareModel)}
                      error={!!errors.hardwareModel}
                      helperText={errors.hardwareModel?.message}
                      size="small"
                      fullWidth
                    >
                      {modelOptions.map((model) => (
                        <MenuItem key={model} value={model}>
                          {model}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  control={control}
                  name="hunter"
                  render={({ field }) => (
                    <HunterAutocomplete
                      value={field.value}
                      onChange={handleHunterChange}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack direction="row" gap={1}>
                  <Controller
                    control={control}
                    name="purchasePrice"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === ""
                              ? null
                              : Number(e.target.value),
                          )
                        }
                        type="number"
                        label={t(RESOURCES.Form.PurchasePrice)}
                        size="small"
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="purchasePriceCurrencyCode"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label={t(RESOURCES.Form.Currency)}
                        size="small"
                        sx={{ minWidth: 90 }}
                      >
                        {CURRENCY_OPTIONS.map((currency) => (
                          <MenuItem key={currency} value={currency}>
                            {currency}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label={t(RESOURCES.Form.Status)}
                      size="small"
                      fullWidth
                    >
                      {statusOptions.map((status) => (
                        <MenuItem key={status} value={status}>
                          {statusLabels[status]}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            </Grid>
            {!isCreate && <TerminalAuditSection publicID={terminal.publicID} />}
          </Stack>
        </DialogContent>
        <DialogActions>
          {/* Deleting is for rows that should never have existed — only stocked pieces qualify
              (mirrors the BE guard), everything placed or in history stays traceable. */}
          {!isCreate &&
            canDelete &&
            terminal.status === TerminalInventoryStatus.InStock && (
              <Button
                color="error"
                onClick={handleDelete}
                disabled={isDeletePending}
                sx={{ mr: "auto" }}
              >
                {t(RESOURCES.Form.Delete)}
              </Button>
            )}
          <Button onClick={onClose}>{t(RESOURCES.Form.Cancel)}</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isAddPending || isUpdatePending || isDeletePending}
          >
            {t(RESOURCES.Form.Save)}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
